import {
    ApiCommerceStandalonePresentationV2Schema,
    type ApiCommerceOrder,
    type ApiCommerceStandaloneOfferV2,
} from "@bridge-applications/pb-types";
import type { TFunction } from "i18next";
import type {
    CommerceRewardCollection,
    CommerceSlice,
} from "../../schemas/slices/CommerceSliceSchema";
import type { RewardCollectionPresentation } from "./rewardCollection";
import { CURRENCY_DECIMAL_SCALE } from "./currencyAmount";
import energySound from "../../assets/static/audio/slot-machine-splash-sound-2.mp3";
import coinSound from "../../assets/static/audio/slot-machine-coins-falling.mp3";
import energyCan from "../../assets/static/images/inbox/energy-can-fat-outlined.png";
import spinWheel from "../../assets/static/images/inbox/spin-wheel-icon.png";
import beachCoins from "../../assets/static/images/holy-wheel/prizes/hw__po__coins.png";
import gram from "../../assets/static/images/coins/style1/coin-style1__gram.webp";
import notcoin from "../../assets/static/images/coins/style1/coin-style1__not.webp";
import usdt from "../../assets/static/images/coins/style1/coin-style1__usdt.webp";

type RewardOffer = Pick<
    ApiCommerceStandaloneOfferV2,
    "offerRevisionId" | "rewards"
> & { presentation?: unknown };

/** The order has no grant lines: only its exact immutable offer revision is safe. */
export function enqueueCommerceRewardCollection(
    current: CommerceSlice["rewardCollection"],
    userId: string,
    order: ApiCommerceOrder,
    offer: RewardOffer,
): CommerceSlice["rewardCollection"] {
    if (
        order.status !== "FULFILLED" ||
        order.offerRevisionId !== offer.offerRevisionId ||
        offer.rewards.length === 0
    )
        return current;

    const session =
        current?.userId === userId
            ? current
            : { userId, queue: [], presentedOrderIds: [] };
    if (session.presentedOrderIds.includes(order.orderId)) return session;

    const parsed = ApiCommerceStandalonePresentationV2Schema.safeParse(
        offer.presentation,
    );
    const dialog = parsed.success ? parsed.data.dialog : undefined;
    const imageUrls =
        dialog?.kind === "BUNDLE"
            ? Object.fromEntries(
                  Object.entries(dialog.prizes).map(([key, prize]) => [
                      key,
                      prize.image.url,
                  ]),
              )
            : {};
    return {
        userId,
        queue: [
            ...session.queue,
            {
                orderId: order.orderId,
                rewards: offer.rewards,
                imageUrls,
            },
        ],
        presentedOrderIds: [...session.presentedOrderIds, order.orderId],
    };
}

const CURRENCY_IMAGES: Readonly<Record<string, string>> = {
    BEACH_COINS: beachCoins,
    GRAM: gram,
    NOT: notcoin,
    USDT: usdt,
};

export function commerceRewardCollection(
    collection: CommerceRewardCollection | null,
    t: TFunction,
): RewardCollectionPresentation | null {
    if (!collection) return null;
    return {
        id: collection.orderId,
        rewards: collection.rewards.map((reward) => {
            const currency = reward.currencyCode ?? "";
            return {
                id: reward.key,
                amount: reward.amount,
                decimalScale:
                    reward.kind === "CURRENCY"
                        ? (CURRENCY_DECIMAL_SCALE[currency] ?? 0)
                        : 0,
                label:
                    reward.kind === "SM_ENERGY"
                        ? t("holyWheel.prizeNames.energy")
                        : reward.kind === "WHEEL_SPINS"
                          ? t("holyWheel.spin")
                          : reward.kind === "CURRENCY"
                            ? currency === "BEACH_COINS"
                                ? t("holyWheel.prizeNames.beach-coins")
                                : currency || t("holyWheel.prizes.item")
                            : t("holyWheel.prizes.item"),
                imageUrl:
                    collection.imageUrls[reward.key] ??
                    (reward.kind === "SM_ENERGY"
                        ? energyCan
                        : reward.kind === "WHEEL_SPINS"
                          ? spinWheel
                          : reward.kind === "CURRENCY"
                            ? CURRENCY_IMAGES[currency]
                            : undefined),
            };
        }),
    };
}

const NO_SOUNDS: readonly string[] = [];
const ENERGY_SOUNDS = [energySound];
const COIN_SOUNDS = [coinSound];
const MIXED_SOUNDS = [energySound, coinSound];
export const COMMERCE_REWARD_SOUNDS: readonly string[] = MIXED_SOUNDS;

/** Stable arrays prevent unrelated catalog updates from restarting playback. */
export function commerceRewardSounds(
    collection: CommerceRewardCollection | null,
): readonly string[] {
    const hasEnergy = collection?.rewards.some(
        (reward) => reward.kind === "SM_ENERGY",
    );
    const hasCoins = collection?.rewards.some(
        (reward) => reward.kind === "CURRENCY",
    );
    return hasEnergy
        ? hasCoins
            ? MIXED_SOUNDS
            : ENERGY_SOUNDS
        : hasCoins
          ? COIN_SOUNDS
          : NO_SOUNDS;
}
